import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { fetchStoryPosts } from '@/lib/storyPosts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** API returns `user` on posts; Word UI historically used `author`. Never render raw objects. */
function storyByline(story) {
  const a = story?.author ?? story?.user;
  if (!a || typeof a !== 'object') {
    return { label: 'Reporter', picture: null };
  }
  const label = [a.display_name, a.name, a.username].find((x) => typeof x === 'string' && x.trim());
  return { label: label || 'Reporter', picture: typeof a.picture === 'string' ? a.picture : null };
}

// Story card for published HBCU.news articles
function StoryCard({ story }) {
  const navigate = useNavigate();
  const { label: bylineName, picture: bylinePicture } = storyByline(story);
  const date = story.created_at
    ? new Date(story.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  return (
    <article
      onClick={() => navigate(`/post/${story.post_id}`)}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--raise)',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        marginBottom: '16px',
        transition: 'border-color 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--fire)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--raise)'}
    >
      {story.media_url && (
        <img
          src={story.media_url}
          alt=""
          style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
          loading="lazy"
        />
      )}
      <div style={{ padding: '16px' }}>
        {story.metadata?.category && (
          <div style={{
            fontFamily: "'Azeret Mono', monospace",
            fontSize: '9px',
            letterSpacing: '2px',
            color: 'var(--fire)',
            textTransform: 'uppercase',
            marginBottom: '6px',
          }}>
            {story.metadata.category}
          </div>
        )}
        <h3 style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: '22px',
          letterSpacing: '1px',
          color: 'var(--cream)',
          lineHeight: '1.1',
          marginBottom: '8px',
        }}>
          {story.metadata?.headline || story.content?.slice(0, 80) || 'Untitled'}
        </h3>
        {story.metadata?.seo_summary && (
          <p style={{ fontSize: '13px', color: 'var(--bone)', lineHeight: '1.5', marginBottom: '12px' }}>
            {story.metadata.seo_summary}
          </p>
        )}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontFamily: "'Azeret Mono', monospace",
          fontSize: '10px',
          color: 'var(--ash)',
        }}>
          {bylinePicture && (
            <img src={bylinePicture} alt="" style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }} />
          )}
          <span>By {bylineName}</span>
          {story.metadata?.school && <><span>·</span><span>{story.metadata.school}</span></>}
          {date && <><span>·</span><span>{date}</span></>}
          <span style={{ marginLeft: 'auto', color: 'var(--fire)' }}>🎙 HBCU.news</span>
        </div>
      </div>
    </article>
  );
}

// Become a Contributor section
function ContributorInfo() {
  return (
    <div style={{
      background: 'var(--deep)',
      border: '1px solid var(--raise)',
      borderLeft: '3px solid var(--fire)',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '24px',
    }}>
      <div style={{
        fontFamily: "'Azeret Mono', monospace",
        fontSize: '9px',
        letterSpacing: '2px',
        color: 'var(--fire)',
        marginBottom: '8px',
      }}>
        HBCU.NEWS CONTRIBUTOR PROGRAM
      </div>
      <h3 style={{
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: '24px',
        color: 'var(--cream)',
        marginBottom: '10px',
      }}>
        YOUR CAMPUS. YOUR BYLINE.
      </h3>
      <p style={{ fontSize: '13px', color: 'var(--bone)', lineHeight: '1.6', marginBottom: '16px' }}>
        File real stories. Get AI-assisted editorial feedback. Publish under a faculty-reviewed byline.
        Build your journalism portfolio one HBCU campus at a time.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        {[
          { tier: 'VERIFIED PRESS', desc: 'Full story + photo submission', color: '#F07B1D' },
          { tier: 'CAMPUS REPORTER', desc: 'Photos + short posts only', color: '#7C4FD4' },
        ].map(t => (
          <div key={t.tier} style={{
            background: 'var(--mid)',
            borderRadius: '6px',
            padding: '12px',
            borderTop: `2px solid ${t.color}`,
          }}>
            <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2px', color: t.color, marginBottom: '4px' }}>
              {t.tier}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--bone)' }}>{t.desc}</div>
          </div>
        ))}
      </div>
      <a
        href="/settings"
        style={{
          display: 'inline-block',
          fontFamily: "'Azeret Mono', monospace",
          fontSize: '10px',
          letterSpacing: '2px',
          color: 'var(--void)',
          background: 'var(--fire)',
          padding: '8px 16px',
          borderRadius: '4px',
          textDecoration: 'none',
        }}
      >
        REQUEST PRESS ACCESS →
      </a>
    </div>
  );
}

export default function WordPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  const isPress = user?.press_tier === 'verified_press' || user?.press_tier === 'campus_reporter';

  const FILTERS = ['all', 'campus', 'culture', 'sports', 'opinion', 'events'];

  useEffect(() => {
    fetchStories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  const fetchStories = async () => {
    setLoading(true);
    try {
      const category = activeFilter === 'all' ? undefined : activeFilter;
      const list = await fetchStoryPosts({ limit: 50, category });
      setStories(Array.isArray(list) ? list : []);
    } catch {
      setStories([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--void)', paddingBottom: '80px' }}>

      {/* Masthead */}
      <div style={{
        background: 'var(--deep)',
        borderBottom: '1px solid var(--raise)',
        padding: '24px 20px 16px',
      }}>
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: '4px',
          }}>
            <div>
              <div style={{
                fontFamily: "'Azeret Mono', monospace",
                fontSize: '8px',
                letterSpacing: '3px',
                color: 'var(--ash)',
                marginBottom: '4px',
              }}>
                POWERED BY BLVX
              </div>
              <h1 style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: '42px',
                letterSpacing: '4px',
                color: 'var(--cream)',
                lineHeight: '1',
              }}>
                HBCU<span style={{ color: 'var(--fire)' }}>.</span>NEWS
              </h1>
            </div>
            {isPress ? (
              <button
                onClick={() => navigate('/word/submit')}
                style={{
                  fontFamily: "'Azeret Mono', monospace",
                  fontSize: '10px',
                  letterSpacing: '2px',
                  background: 'var(--fire)',
                  color: 'var(--void)',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 16px',
                  cursor: 'pointer',
                }}
              >
                + SUBMIT STORY
              </button>
            ) : (
              <div style={{
                fontFamily: "'Azeret Mono', monospace",
                fontSize: '8px',
                letterSpacing: '2px',
                color: 'var(--ash)',
                textAlign: 'right',
                lineHeight: '1.5',
              }}>
                🎙 STUDENT JOURNALISM<br />ON THE YARD
              </div>
            )}
          </div>
          <p style={{
            fontFamily: "'Azeret Mono', monospace",
            fontSize: '9px',
            letterSpacing: '2px',
            color: 'var(--ash)',
          }}>
            THE HBCU STUDENT PRESS — FACULTY REVIEWED — REAL BYLINES
          </p>
        </div>
      </div>

      {/* Category filters */}
      <div style={{
        borderBottom: '1px solid var(--raise)',
        background: 'var(--deep)',
        overflowX: 'auto',
      }}>
        <div style={{
          maxWidth: '680px',
          margin: '0 auto',
          display: 'flex',
          gap: '0',
          padding: '0 20px',
        }}>
          {FILTERS.map(f => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              style={{
                fontFamily: "'Azeret Mono', monospace",
                fontSize: '9px',
                letterSpacing: '2px',
                color: activeFilter === f ? 'var(--fire)' : 'var(--ash)',
                background: 'none',
                border: 'none',
                borderBottom: activeFilter === f ? '2px solid var(--fire)' : '2px solid transparent',
                padding: '12px 14px',
                cursor: 'pointer',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '20px' }}>

        {/* Contributor info for non-press users */}
        {!isPress && <ContributorInfo />}

        {/* Stories */}
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 0',
            fontFamily: "'Azeret Mono', monospace",
            fontSize: '10px',
            letterSpacing: '2px',
            color: 'var(--ash)',
          }}>
            LOADING STORIES...
          </div>
        ) : stories.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'var(--surface)',
            borderRadius: '8px',
            border: '1px solid var(--raise)',
          }}>
            <div style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '28px',
              color: 'var(--bone)',
              letterSpacing: '2px',
              marginBottom: '8px',
            }}>
              THE NEWSROOM IS OPEN
            </div>
            <p style={{ fontSize: '13px', color: 'var(--ash)', marginBottom: '20px' }}>
              No stories published yet. Be the first reporter on the yard.
            </p>
            {isPress ? (
              <button
                onClick={() => navigate('/word/submit')}
                style={{
                  fontFamily: "'Azeret Mono', monospace",
                  fontSize: '10px',
                  letterSpacing: '2px',
                  background: 'var(--fire)',
                  color: 'var(--void)',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                }}
              >
                SUBMIT FIRST STORY
              </button>
            ) : (
              <a
                href="/settings"
                style={{
                  fontFamily: "'Azeret Mono', monospace",
                  fontSize: '10px',
                  letterSpacing: '2px',
                  color: 'var(--fire)',
                  textDecoration: 'none',
                }}
              >
                REQUEST PRESS ACCESS →
              </a>
            )}
          </div>
        ) : (
          stories.map(s => <StoryCard key={s.post_id} story={s} />)
        )}
      </div>
    </div>
  );
}
